import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import localforage from "localforage";
import { useOffline } from "@/context/OfflineContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import SyncIndicator from "@/components/SyncIndicator";
import { 
  Plus, 
  Search, 
  Filter,
  ChevronRight,
  ChevronLeft,
  FileText,
  ArrowLeft,
  WifiOff,
  Cloud
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClaimsList = () => {
  const navigate = useNavigate();
  const { isOnline, pendingSync } = useOffline();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce search to avoid firing on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      if (isOnline) {
        const params = { page, page_size: 20 };
        if (statusFilter !== "all") params.status = statusFilter;
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

        const response = await axios.get(`${API}/claims`, { params });
        const data = response.data;
        setClaims(data.items || []);
        setTotalPages(data.pages || 1);
        setTotal(data.total || 0);
        // Cache for offline
        await localforage.setItem("cached_claims", data.items || []);
      } else {
        // Offline: use cached claims with client-side filtering
        const cached = (await localforage.getItem("cached_claims")) || [];
        let filtered = cached;
        if (statusFilter !== "all") {
          filtered = filtered.filter((c) => c.status === statusFilter);
        }
        if (debouncedSearch.trim()) {
          const s = debouncedSearch.toLowerCase();
          filtered = filtered.filter(
            (c) =>
              (c.file_number || "").toLowerCase().includes(s) ||
              (c.client_name || "").toLowerCase().includes(s) ||
              (c.address || "").toLowerCase().includes(s)
          );
        }
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setClaims(filtered);
        setTotalPages(1);
        setTotal(filtered.length);
      }
    } catch (error) {
      // Fallback to cache on network error
      const cached = (await localforage.getItem("cached_claims")) || [];
      setClaims(cached);
      setTotalPages(1);
      setTotal(cached.length);
      toast.error("Erreur lors du chargement des dossiers");
    } finally {
      setLoading(false);
    }
  }, [isOnline, page, statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  // Merge local pending claims (offline-created) on top
  const [localClaims, setLocalClaims] = useState([]);
  useEffect(() => {
    localforage.getItem("pending_claims").then((pending) => {
      setLocalClaims(pending || []);
    });
  }, [isOnline]);

  const localIds = new Set(localClaims.map((c) => c.id));
  const displayClaims = [
    ...localClaims.map((c) => ({ ...c, _isLocal: true })),
    ...claims.filter((c) => !localIds.has(c.id)),
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="claims-list">
      {/* Offline indicator */}
      {!isOnline && (
        <div className="bg-orange-500 text-white text-center py-2 text-sm font-medium flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" />
          Mode hors ligne - {pendingSync > 0 ? `${pendingSync} élément(s) en attente` : "Données locales"}
        </div>
      )}
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold text-slate-900">Dossiers</h1>
              {!loading && (
                <span className="text-sm text-slate-400 font-normal" data-testid="total-count">
                  ({total})
                </span>
              )}
            </div>
            <Button
              onClick={() => navigate("/claims/new")}
              className="bg-sky-600 hover:bg-sky-700 h-12"
              data-testid="new-claim-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nouveau
            </Button>
          </div>

          {/* Search and Filter */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-12 pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-12" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="draft">Brouillons</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="completed">Terminés</SelectItem>
                <SelectItem value="archived">Archivés</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayClaims.length > 0 ? (
          <>
            <div className="space-y-3">
              {displayClaims.map((claim) => (
                <Card
                  key={claim.id}
                  onClick={() => navigate(`/claims/${claim.id}`)}
                  className="cursor-pointer transition-shadow hover:shadow-md"
                  data-testid={`claim-item-${claim.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-bold text-slate-900">
                            {claim.file_number || "Nouveau"}
                          </span>
                          <StatusBadge status={claim.status} />
                          {claim._isLocal && (
                            <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-700 flex items-center gap-1">
                              <Cloud className="w-3 h-3" />
                              Local
                            </span>
                          )}
                        </div>
                        <p className="text-base font-medium text-slate-700 truncate">
                          {claim.client_name}
                        </p>
                        <p className="text-sm text-slate-500 truncate">
                          {claim.address}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {format(new Date(claim.created_at), "dd/MM/yyyy à HH:mm", { locale: fr })}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 ml-3" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 px-1" data-testid="pagination">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="h-10"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Précédent
                </Button>

                <span className="text-sm text-slate-600" data-testid="pagination-info">
                  Page {page} sur {totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="h-10"
                  data-testid="pagination-next"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg mb-2">Aucun dossier trouvé</p>
            <p className="text-slate-400 text-sm mb-6">
              {search || statusFilter !== "all"
                ? "Modifiez vos critères de recherche"
                : "Créez votre premier dossier sinistre"}
            </p>
            {!search && statusFilter === "all" && (
              <Button
                onClick={() => navigate("/claims/new")}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="empty-new-claim-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Créer un dossier
              </Button>
            )}
          </div>
        )}
      </main>

      <SyncIndicator />
    </div>
  );
};

export default ClaimsList;
